import { Input, InputNumber, Popover, Space, Table } from 'antd'
import { red } from '@ant-design/colors'
import { InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { centsToDollarsDisplay, percentageDisplayWhole } from '../displayFormatters'
import { PaymentScheduleAction, toPaymentScheduleRows } from './paymentScheduleModel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-solid-svg-icons'
import { PaymentScheduleRow } from '../../domain/proposals/settings/proposalSettings'

export type PaymentScheduleProps = {
  paymentSchedule: PaymentScheduleRow[]
  total: number,
  dispatch: (a: PaymentScheduleAction) => void,
  hideAmounts?: boolean
}

const PaymentSchedule = ({ paymentSchedule, total, dispatch, hideAmounts }: PaymentScheduleProps) => {
  const renderTitle = (value: any, _record: PaymentScheduleRow, index: number) =>
    <Input
      value={value}
      onChange={e => dispatch({ type: 'updatePaymentTitle', index, value: e.target.value })}
    />
  const renderNote = (value: any, _record: PaymentScheduleRow, index: number) =>
    <Input
      value={value}
      onChange={e => dispatch({ type: 'updatePaymentNote', index, value: e.target.value })}
    />
  const renderPercentage = (value: any, record: PaymentScheduleRow, index: number) => {
    const percent = percentageDisplayWhole(value)!
    const percentWithoutSymbol = percent.replace('%', '')
    return record.canUpdatePercentage
      ? <Space>
        <InputNumber
          value={percentWithoutSymbol}
          style={{ maxWidth: '100px' }}
          addonAfter="%"
          onChange={value => {
            dispatch({ type: 'updatePaymentPercentage', index, value: value ?? '' })
          }}
        />
        {index === 0 &&
          <Popover content={<div style={{ width: "350px" }}>
            You can only collect 100% down payments on Quoted workareas.
            If you have Flat Rate pricing or Time and Materials pricing in this proposal,
            you will not be able to include those workareas in a proposal with a 100% down payment.
          </div>}>
            <InfoCircleOutlined />
          </Popover>
        }
      </Space>
      : percent
  }
  const renderAmount = (value: any) => centsToDollarsDisplay(value)
  const renderControls = (_: any, record: PaymentScheduleRow, index: number) => {
    return <Space size="large">
      {index < paymentSchedule.length - 1 &&
        <PlusCircleOutlined
          data-testid={`add-payment-${index}`}
          onClick={() => dispatch({ type: 'addPayment', index })}
        />
      }
      {record.canDelete &&
        <span
          data-testid={`delete-payment-${index}`}
          onClick={() => dispatch({ type: 'deletePayment', index })}
        >
          <FontAwesomeIcon icon={faTrash} style={{ color: red.primary, cursor: 'pointer' }} />
        </span>
      }
    </Space>
  }
  const summary =
    <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: '500' }}>
      <Table.Summary.Cell index={0}></Table.Summary.Cell>
      <Table.Summary.Cell index={1}></Table.Summary.Cell>
      <Table.Summary.Cell index={2}>Total</Table.Summary.Cell>
      <Table.Summary.Cell index={3}>{centsToDollarsDisplay(total)}</Table.Summary.Cell>
      <Table.Summary.Cell index={4}></Table.Summary.Cell>
    </Table.Summary.Row>

  const columns = [
    { title: 'Title', dataIndex: 'title', key: 'title', render: renderTitle },
    { title: 'Note', dataIndex: 'note', key: 'note', render: renderNote },
    { title: 'Percentage', dataIndex: 'percentage', key: 'percentage', render: renderPercentage },
    ...(hideAmounts ? [] : [
      { title: 'Amount', dataIndex: 'amount', key: 'amount', render: renderAmount },
    ]),
    { title: '', key: 'controls', render: renderControls }
  ]
  return <Table columns={columns}
    dataSource={toPaymentScheduleRows(paymentSchedule)}
    size="small"
    summary={hideAmounts ? undefined : () => summary}
    pagination={false}
    rowKey="sequence" />
}
export default PaymentSchedule
